import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CalendarRange, Lock } from "lucide-react";
import { useContentDefaults } from "@/components/content/DraftBuilderModal";

const Toggle = ({ label, sub, checked, onChange, disabled, testid }) => (
  <label className={`flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/20 p-3 ${disabled ? "opacity-50" : ""}`}>
    <span><span className="text-sm font-medium block">{label}</span><span className="text-xs text-muted-foreground">{sub}</span></span>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </label>
);

export default function BlogCampaignModal({ open, onOpenChange, keywords, metrics, workspace, onUpgrade }) {
  const navigate = useNavigate();
  const d = useContentDefaults(open ? workspace?.id : null);
  const [target, setTarget] = useState("");
  const [cta, setCta] = useState("");
  const [months, setMonths] = useState(1);
  const [shopify, setShopify] = useState(true);
  const [inapp, setInapp] = useState(true);
  const [promote, setPromote] = useState(true);
  const [ppc, setPpc] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (d && !target) { setTarget(d.target_url || ""); setCta(d.target_url || ""); } }, [d]); // eslint-disable-line

  const pickMonths = (m) => {
    if (d && m > d.max_months) { onUpgrade?.({ kind: "blog", message: m === 6 ? "6-month blog campaigns are on Agency ($199/mo)." : "Blog campaigns are on E-Commerce ($79/mo) and Agency ($199/mo)." }); return; }
    setMonths(m);
  };

  const create = async () => {
    if (d && !d.blog_allowed) { onUpgrade?.({ kind: "blog", message: "Daily blog campaigns are on E-Commerce ($79/mo) and Agency ($199/mo)." }); return; }
    if (!target.trim()) { toast.error("Add your website URL"); return; }
    setBusy(true);
    try {
      const r = await api.post("/content-campaigns", { workspace_id: workspace.id, keywords, metrics, target_url: target, cta_url: cta || target, months, publish_shopify: shopify && !!d?.shopify_connected, publish_inapp: inapp || !d?.shopify_connected, promote_social: promote, include_ppc: ppc, origin_url: window.location.origin });
      toast.success(`${r.data.items} daily blogs scheduled`, { description: "Articles are written 24h before they publish." });
      onOpenChange(false);
      navigate(`/content?campaign=${r.data.campaign.id}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 403 && det?.message) onUpgrade?.({ kind: "blog", message: det.message });
      else toast.error(typeof det === "string" ? det : (det?.message || "Could not create the campaign"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card border-border max-h-[92vh] overflow-y-auto" data-testid="blog-campaign-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CalendarRange className="h-5 w-5 text-primary" /> Daily blog campaign</DialogTitle>
          <DialogDescription>One SEO article a day across {keywords.length} keywords, published automatically and shown on your calendar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2" data-testid="campaign-months">
            {[1, 3, 6].map((m) => (
              <button key={m} type="button" onClick={() => pickMonths(m)} data-testid={`campaign-months-${m}`}
                className={`rounded-lg border p-3 text-sm transition-colors ${months === m ? "border-primary bg-primary/10" : "border-border bg-secondary/20 hover:border-primary/40"}`}>
                <span className="font-semibold block">{m} month{m > 1 ? "s" : ""}</span>
                <span className="text-xs text-muted-foreground">{m * 30} articles{d && m > d.max_months ? <Lock className="inline h-3 w-3 ml-1 text-amber-300" /> : null}</span>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Website</Label><Input value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 bg-background" data-testid="campaign-target-url" /></div>
            <div><Label className="text-xs">CTA link in every article</Label><Input value={cta} onChange={(e) => setCta(e.target.value)} className="mt-1 bg-background" data-testid="campaign-cta-url" /></div>
          </div>
          <Toggle testid="campaign-shopify-toggle" label="Publish to Shopify blog" sub={d?.shopify_connected ? `Posts to ${d.shop_url}` : "Connect Shopify in Settings to enable"} checked={shopify && !!d?.shopify_connected} onChange={setShopify} disabled={!d?.shopify_connected} />
          <Toggle testid="campaign-inapp-toggle" label="Publish to your AutoGridSocial blog page" sub="A public blog hosted for you" checked={inapp || !d?.shopify_connected} onChange={setInapp} disabled={!d?.shopify_connected} />
          <Toggle testid="campaign-promote-toggle" label="Promote each article on Facebook & LinkedIn" sub="A linked post is queued 2h after each article goes live" checked={promote} onChange={setPromote} />
          <Toggle testid="campaign-ppc-toggle" label="Build matching Google + Meta ads" sub="Export-ready ad groups and ad sets from the same keywords" checked={ppc} onChange={setPpc} />
          <Button onClick={create} disabled={busy} className="w-full h-11 bg-primary hover:bg-primary/90 text-white" data-testid="campaign-create-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading your site…</> : `Schedule ${months * 30} daily articles`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
